import type {
  ColumnDataType,
  DatabaseBlockModel,
  ListBlockModel,
} from '@blocksuite/affine/model';
import type { Store } from '@blocksuite/affine/store';
import { Text as BlocksuiteText } from '@blocksuite/affine/store';
import { Text as YText } from 'yjs';

import type { TaskItem, TaskSource } from './types';
import { statusLabelToBucket } from './types';

export type ExtractedTask = Omit<TaskItem, 'docTitle' | 'updatedAt'>;

const STATUS_COLUMN_NAMES = new Set(['status', 'статус']);

type DeltaLike = { attributes?: { mention?: { member?: string } | null } | null };

function textHasMention(
  text: BlocksuiteText | YText | null | undefined,
  userId: string
): boolean {
  if (!text) return false;
  try {
    const delta = text.toDelta() as unknown as DeltaLike[];
    return delta.some(d => d.attributes?.mention?.member === userId);
  } catch {
    return false;
  }
}

function cellValueHasMention(value: unknown, userId: string): boolean {
  if (value instanceof BlocksuiteText || value instanceof YText) {
    return textHasMention(value, userId);
  }
  return false;
}

type SelectLikeColumn = ColumnDataType<{
  options?: { id: string; value: string }[];
}>;

function isSelectLike(column: ColumnDataType): boolean {
  return column.type === 'select' || column.type === 'multi-select';
}

function isDateLike(column: ColumnDataType): boolean {
  return column.type === 'date' || column.type === 'date-range';
}

function selectCellToLabel(
  value: unknown,
  column: SelectLikeColumn
): string | null {
  const options = column.data?.options ?? [];
  const toLabel = (optionId: unknown): string | null => {
    if (typeof optionId !== 'string' || !optionId) return null;
    return options.find(o => o.id === optionId)?.value ?? optionId;
  };
  if (Array.isArray(value)) {
    const labels = value.map(toLabel).filter((v): v is string => !!v);
    return labels.length ? labels.join(', ') : null;
  }
  return toLabel(value);
}

function normalizeDateValue(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (
    typeof value === 'object' &&
    value !== null &&
    'start' in value &&
    typeof (value as { start: unknown }).start === 'number'
  ) {
    return (value as { start: number }).start;
  }
  return null;
}

function pickDueDate(
  cells: Record<string, { columnId: string; value: unknown } | undefined>,
  dateColumns: ColumnDataType[]
): number | null {
  for (const column of dateColumns) {
    const due = normalizeDateValue(cells[column.id]?.value);
    if (due !== null) return due;
  }
  return null;
}

function makeTask(
  docId: string,
  blockId: string,
  source: TaskSource,
  title: string,
  statusLabel: string | null
): ExtractedTask {
  const bucket = statusLabelToBucket(statusLabel);
  return {
    id: `${docId}:${blockId}`,
    docId,
    blockId,
    source,
    title: title.trim(),
    status: bucket,
    statusLabel: statusLabel ?? null,
    done: bucket === 'done',
    due: null,
  };
}

/**
 * Extracts tasks mentioning `userId` from a loaded (block-suite) doc.
 *
 * - `affine:list` blocks with `type: 'todo'` whose text contains an inline
 *   mention of the user (outside of databases — rows are covered below).
 * - Rows (children) of `affine:database` blocks whose title or rich-text cell
 *   contains a mention. Status is read from the first select column named
 *   "Status"/"Статус" (falling back to the first select column), due date from
 *   the first date column with a value.
 */
export function extractTasksFromDoc(
  store: Store,
  docId: string,
  userId: string
): ExtractedTask[] {
  if (!userId) return [];

  const tasks: ExtractedTask[] = [];

  // Database rows
  const databaseModels = store.getModelsByFlavour(
    'affine:database'
  ) as DatabaseBlockModel[];
  const rowIds = new Set<string>();

  for (const database of databaseModels) {
    const columns: ColumnDataType[] = database.props.columns ?? [];
    const selectColumns = columns.filter(isSelectLike);
    const statusColumn =
      selectColumns.find(c => STATUS_COLUMN_NAMES.has(c.name.trim().toLowerCase())) ??
      selectColumns[0];
    const dateColumns = columns.filter(isDateLike);

    for (const row of database.children) {
      rowIds.add(row.id);

      const cells = (database.props.cells?.[row.id] ?? {}) as Record<
        string,
        { columnId: string; value: unknown } | undefined
      >;

      const title = row.text?.toString() ?? '';
      const hasMention =
        textHasMention(row.text, userId) ||
        Object.values(cells).some(cell => cellValueHasMention(cell?.value, userId));
      if (!hasMention) continue;

      const task = makeTask(
        docId,
        row.id,
        'database',
        title,
        statusColumn
          ? selectCellToLabel(cells[statusColumn.id]?.value, statusColumn as SelectLikeColumn)
          : null
      );
      task.due = pickDueDate(cells, dateColumns);
      tasks.push(task);
    }
  }

  // Standalone todo blocks
  const listModels = store.getModelsByFlavour(
    'affine:list'
  ) as ListBlockModel[];
  for (const list of listModels) {
    if (list.props.type !== 'todo') continue;
    // rows of a database are handled above
    if (rowIds.has(list.id)) continue;
    if (!textHasMention(list.text, userId)) continue;

    const checked = !!list.props.checked;
    const task = makeTask(docId, list.id, 'todo', list.text?.toString() ?? '', null);
    task.status = checked ? 'done' : 'todo';
    task.statusLabel = null;
    task.done = checked;
    tasks.push(task);
  }

  return tasks;
}
